import clsx from "clsx";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";

import { isVscode } from "@tsmono/util";

import { useEventListener } from "../hooks/useEventListener";
import { useProperty } from "../hooks/useProperty";

import { useComponentNavigation } from "./ComponentNavigationContext";
import { isNewTabClick } from "./inAppLink";
import { MarkdownDiv, type MarkdownRenderer } from "./MarkdownDiv";
import styles from "./MarkdownDivWithReferences.module.css";
import { escapeHtmlCharacters } from "./markdownText";
import { NoContentsPanel } from "./NoContentsPanel";
import { PopOver } from "./PopOver";

export interface MarkdownReference {
  id: string;
  cite: string;
  citePreview?: () => React.ReactNode;
  citeUrl?: string;
}

interface MarkdownDivWithReferencesProps {
  markdown: string;
  references?: MarkdownReference[];
  options?: {
    previewRefsOnHover?: boolean;
  };
  className?: string | string[];
  style?: React.CSSProperties;
  renderer?: MarkdownRenderer;
  /** See `MarkdownDiv`'s `truncateAt`. */
  truncateAt?: number;
}

export const MarkdownDivWithReferences = forwardRef<
  HTMLDivElement,
  MarkdownDivWithReferencesProps
>((props, ref) => {
  const { markdown, references, options, className, style, renderer } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [positionEl, setPositionEl] = useState<HTMLElement | null>(null);
  const [currentRef, setCurrentRef] = useState<MarkdownReference | null>(null);

  const [visibleKey, setVisibleKey, clearVisibleKey] = useProperty<string>(
    "popover",
    "visibleKey"
  );

  // Create a map for quick lookup of references by ID
  const refMap = useMemo(
    () => new Map(references?.map((r) => [r.id, r])),
    [references]
  );
  const hasReferences = (references?.length || 0) > 0;

  const { navigate } = useComponentNavigation();

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const anchor = e.target instanceof Element ? e.target.closest("a") : null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        // If this is a hash link, forward on to react-router
        // so it can see this navigate. Cite links are in-view navigation
        // (jumping to a referenced event/message in the same transcript),
        // so use replace to avoid filling history with each click.
        // New-tab gestures open the real href natively (except in the VS
        // Code webview, which has no browser tabs).
        if (href?.startsWith("#/") && (isVscode() || !isNewTabClick(e))) {
          e.preventDefault();
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          navigate(href.slice(1), { replace: true });
        }
      }
    },
    [navigate]
  );

  // Post-process the rendered HTML to inject reference links
  const postProcess = useCallback(
    (html: string): string =>
      injectReferenceLinks(html, references, styles.cite),
    [references]
  );

  // Memoize the MarkdownDiv to prevent re-renders when popover state changes
  // This keeps the DOM stable so event handlers remain attached
  const memoizedMarkdown = useMemo(
    () => (
      <MarkdownDiv
        ref={ref}
        markdown={markdown}
        postProcess={hasReferences ? postProcess : undefined}
        style={style}
        renderer={renderer}
        truncateAt={props.truncateAt}
        onClick={handleLinkClick}
      />
    ),
    [
      ref,
      markdown,
      hasReferences,
      postProcess,
      style,
      renderer,
      props.truncateAt,
      handleLinkClick,
    ]
  );

  // Use event delegation so handlers work even when cite links are injected
  // asynchronously (MarkdownDiv renders via an async queue, so the initial
  // DOM has no cite links — a per-link attach would miss them entirely).
  //
  // `suppressedIdRef` holds the ref-id of a just-clicked cite link.
  // Re-render after click creates fresh DOM, which would otherwise fire a
  // new mouseover and re-show the popover even though the user just
  // dismissed it. Suppress shows for that ref-id until the mouse actually
  // leaves the link (mouseout transition off the link element).
  const suppressedIdRef = useRef<string | null>(null);
  // Don't enable popover / preview on hover
  const delegationTarget =
    options?.previewRefsOnHover === false ? null : containerRef;

  // mouseover bubbles (mouseenter doesn't) — we filter to transitions
  // into the cite link by checking relatedTarget.
  useEventListener(delegationTarget, "mouseover", (e) => {
    const el = findCiteLink(e.target);
    if (!el) return;
    const related = e.relatedTarget;
    if (related instanceof Node && el.contains(related)) return;

    const id = el.getAttribute("data-ref-id");
    if (!id) return;
    if (suppressedIdRef.current === id) return;
    const r = refMap.get(id);
    if (!r || !r.citePreview) return;

    // PopOver handles show/hide logic including hover delays.
    setPositionEl(el);
    setCurrentRef(r);
    setVisibleKey(popoverKey(r));
  });

  // mouseout bubbles too. Clear suppression once the mouse leaves the
  // suppressed cite link entirely (i.e. relatedTarget isn't a descendant).
  useEventListener(delegationTarget, "mouseout", (e) => {
    const el = findCiteLink(e.target);
    if (!el) return;
    const id = el.getAttribute("data-ref-id");
    if (!id || suppressedIdRef.current !== id) return;
    const related = e.relatedTarget;
    if (related instanceof Node && el.contains(related)) return;
    suppressedIdRef.current = null;
  });

  useEventListener(delegationTarget, "click", (e) => {
    const el = findCiteLink(e.target);
    if (!el) return;

    // Cancel the popover if one is pending or showing, and suppress it
    // for this ref-id until the mouse leaves the link — re-render after
    // click would otherwise fire a fresh mouseover and re-open it.
    clearVisibleKey();
    setCurrentRef(null);
    setPositionEl(null);
    suppressedIdRef.current = el.getAttribute("data-ref-id");

    // Stop propagation to prevent parent Link components from handling.
    e.stopPropagation();
  });

  const key = currentRef
    ? popoverKey(currentRef)
    : "unknown-markdown-ref-popover";

  return (
    <div className={clsx(className)} ref={containerRef}>
      {memoizedMarkdown}
      {positionEl && currentRef && (
        <PopOver
          id={key}
          positionEl={positionEl}
          isOpen={visibleKey === key}
          setIsOpen={(isOpen) => {
            if (!isOpen) {
              clearVisibleKey();
              setCurrentRef(null);
              setPositionEl(null);
            }
          }}
          placement="auto"
          hoverDelay={1000}
          showArrow={true}
          styles={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {(currentRef.citePreview && currentRef.citePreview()) || (
            <NoContentsPanel text="No preview available." />
          )}
        </PopOver>
      )}
    </div>
  );
});

MarkdownDivWithReferences.displayName = "MarkdownDivWithReferences";

const popoverKey = (ref: MarkdownReference) => `markdown-ref-popover-${ref.id}`;

const citeSelector = `.${styles.cite}`;

const findCiteLink = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof Element)) return null;
  return target.closest<HTMLElement>(citeSelector);
};

/**
 * Replace bracketed M/E ordinals in HTML with anchor links for known references.
 *
 * Matches bracket expressions like [M1], [M1-M3], [M2, M4] and replaces
 * each known ordinal inside with an `<a>` link.
 */
export function injectReferenceLinks(
  html: string,
  references: MarkdownReference[] | undefined,
  citeClass: string
): string {
  // Build a map of known ordinals (e.g. "M6") from references
  const refByOrdinal = new Map<string, MarkdownReference>();
  references?.forEach((ref) => {
    const ordinal = ref.cite.replace(/^\[|\]$/g, "");
    refByOrdinal.set(ordinal, ref);
  });

  if (refByOrdinal.size === 0) {
    return html;
  }

  const linkOrdinals = (bracket: string): string =>
    bracket.replace(/\b[ME]\d+\b/g, (ordinal) => {
      const ref = refByOrdinal.get(ordinal);
      if (!ref) return ordinal;
      // The id and URL come from log content; escaping keeps them inside the
      // attribute rather than leaving DOMPurify to repair a quote breakout.
      const id = escapeHtmlCharacters(ref.id);
      const cls = escapeHtmlCharacters(citeClass);
      // No URL means nowhere to navigate: a span, not a link that can't be
      // followed or opened in a new tab (the popover still works by class).
      return ref.citeUrl
        ? `<a href="${escapeHtmlCharacters(ref.citeUrl)}" class="${cls}" data-ref-id="${id}">${ordinal}</a>`
        : `<span class="${cls}" data-ref-id="${id}">${ordinal}</span>`;
    });

  // Link the ordinals inside every bracket expression that holds at least one.
  // Equivalent to html.replace(/\[[^\]]*(?:M|E)\d+[^\]]*\]/g, linkOrdinals),
  // but driven by indexOf: every `[` ahead of a given `]` pairs with that same
  // `]`, so each candidate bracket is inspected once (the regex rescanned from
  // every `[`, quadratic on a run of `[` or of ordinals with no closing `]`).
  let out = "";
  let emitted = 0;
  let search = 0;
  for (;;) {
    const open = html.indexOf("[", search);
    if (open === -1) break;
    const close = html.indexOf("]", open + 1);
    if (close === -1) break;
    const bracket = html.slice(open, close + 1);
    if (/[ME]\d/.test(bracket)) {
      out += html.slice(emitted, open) + linkOrdinals(bracket);
      emitted = close + 1;
    }
    search = close + 1;
  }
  return emitted === 0 ? html : out + html.slice(emitted);
}
